"use client"

import type React from "react"

import { useState } from "react"
import { motion } from "framer-motion"
import { Mail, Phone, Users, CheckCircle, AlertCircle, Send, X, Check } from "lucide-react"
import ImagesDev from "../../api/images"
import Link from "../../components/Link"
import { Toast } from "../../components/Toast"

interface FormData {
  email_address: string
  phone_number: string
  volunteer: string
  first_name: string
  surname: string
  vip: string
  isEmailAvailable: boolean
  isPhoneValid: boolean
}

interface FormErrors {
  email_address?: string
  phone_number?: string
  volunteer?: string
  first_name?: string
  surname?: string
  vip?: string
  isEmailAvailable?: boolean
  isPhoneValid?: boolean
}

export default function Volunteer_subComponent({ email_address, phone_number }: any) {
  // const [formData_, setFormData_] = useState<FormData>({
  //   email_address: "",
  //   phone_number: "",
  //   volunteer: "",
  // })
  const [formData, setFormData] = useState<FormData>({
    first_name: "",
    surname: "",
    email_address: email_address,
    phone_number: phone_number,
    volunteer: "",
    vip: "false",
    isEmailAvailable: false,
    isPhoneValid: false,
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [userId, setUserId] = useState<string | null>(null)
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  const [showVolunteer, setShowVolunteer] = useState(false);

  const roles = [
    { id: "protocol", label: "Protocol", description: "Manage event procedures and coordination" },
    { id: "hospitality", label: "Hospitality", description: "Assist guests with comfort, refreshments, and directions" },
    { id: "front-desk", label: "Front Desk", description: "Welcome guests and handle registration" },
    { id: "usher", label: "Ushering", description: "Guide attendees and maintain order" },
    { id: "media", label: "Media", description: "Handle photography and social media" },
  ]

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePhone_number = (number: string): boolean => {
    // Remove all non-digit characters
    const cleanNumber = number.replace(/\D/g, "")

    // WhatsApp numbers should be between 10-15 digits
    // Most international numbers are 10-14 digits, but allowing up to 15 for flexibility
    if (cleanNumber.length < 10 || cleanNumber.length > 15) {
      return false
    }

    // Check if it starts with a country code (1-4 digits)
    // Most common patterns: +1, +44, +91, +234, etc.
    const hasValidCountryCode = /^(\+?[1-9]\d{0,3})?[1-9]\d{8,13}$/.test(number.replace(/[\s\-$$$$]/g, ""))

    return hasValidCountryCode
  }

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.email_address) {
      newErrors.email_address = "Email is required"
    } else if (!validateEmail(formData.email_address)) {
      newErrors.email_address = "Please enter a valid email address"
    }

    if (!formData.phone_number) {
      newErrors.phone_number = "WhatsApp number is required"
    } else if (!validatePhone_number(formData.phone_number)) {
      newErrors.phone_number = "Please enter a valid WhatsApp number with country code"
    }

    if (!formData.volunteer) {
      newErrors.volunteer = "Please select a volunteer role"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // const handleSubmit = async (e: React.FormEvent) => {
  //   e.preventDefault()

  //   if (!validateForm()) return

  //   setIsSubmitting(true)

  //   // Simulate API call
  //   await new Promise((resolve) => setTimeout(resolve, 2000))

  //   setIsSubmitting(false)
  //   setIsSubmitted(true)

  //   // Reset form after success
  //   setTimeout(() => {
  //     // setFormData({ email_address: "", phone_number: "", volunteer: "", vip: "",  })
  //     // setIsSubmitted(false)
  //   }, 3000)
  // }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Step 1: Check if user exists
      const res = await fetch(
        `https://tsoministries.com/wp-json/custom-form/v1/thp-by-email?email=${encodeURIComponent(formData.email_address)}&t=${Date.now()}`
      );

      if (!res.ok) {
        const errorText = await res.text();
        console.error("Failed to check user existence:", res.status, errorText);
        // alert("We couldn't verify your email. Please try again.");
        Toast.error("An unexpected error occurred. Please check your internet connection or try again later.");
        return;
      }

      const data = await res.json();

      if (data?.email_address) {
        // User found — Update
        const updatePayload = {
          phone_number: formData.phone_number,
          volunteer: formData.volunteer,
        };

        const updateRes = await fetch(
          `https://tsoministries.com/wp-json/custom-form/v1/thp-update/${data.id}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(updatePayload),
          }
        );

        if (!updateRes.ok) {
          const errorBody = await updateRes.text();
          console.error("Update failed:", updateRes.status, errorBody);
          Toast.error("Something went wrong. Please try again later.");
          return;
        }

        // Success
        setUserId(data.id);
        setIsSubmitted(true);
      } else {
        // User not found — Show prompt
        Toast.error("An unexpected error occurred. Please check your internet connection or try again later.");
      }
    } catch (err) {
      console.error("Unexpected error during submission:", err);
      Toast.error("An unexpected error occurred. Please check your internet connection or try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
      >
        <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-red-500/10 h-full flex flex-col justify-center items-center">
          <div className="flex flex-col items-center justify-center p-4 relative gap-2">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white/10 rounded-2xl shadow-xl p-8 text-center max-w-md w-full relative"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              </motion.div>
              <h2 className="text-2xl font-bold text-gray-100 mb-2">You are all set!</h2>
              <p className="text-gray-100/80">
                Your volunteer application has been submitted successfully. We'll contact you soon!
              </p>
            </motion.div>
            <div className="text-center">
              <p className="text-gray-400">
                The Healing Party - TSO Ministries
                <br />
                <span className="text-orange-700 font-semibold">August 28, 2026 • 02:00 PM WAT</span>
              </p>
            </div>
          </div>
        </form>
      </motion.div>
    )
  }

  const renderVolunteer = () => {
    return (
      <div className="py-0 px-4 relative">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-gray-100/10 rounded-2xl shadow-xl p-8 pt-2"
        >
          <form className="space-y-6">
            {/* email_address Field */}
            <input
              type="email"
              value={formData.email_address}
              onChange={(e) => handleInputChange("email_address", e.target.value)}
              className={`hidden w-full pl-10 pr-4 py-3 border-2 rounded-lg bg-black focus:outline-none transition-colors ${errors.email_address ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-orange-500"
                }`}
              placeholder="your.email@example.com"
            />

            {/* phone_number Field */}
            <input
              type="tel"
              value={formData.phone_number}
              onChange={(e) => handleInputChange("phone_number", e.target.value)}
              className={`hidden w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${errors.phone_number ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-orange-500"
                }`}
              placeholder="+1 234 567 8900"
            />

            {/* Role Selection */}
            <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.5 }}>
              <label className="block text-sm font-semibold text-gray-100 mb-4">
                <Users className="inline w-5 h-5 mr-2" />
                Select Your Volunteer Role
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {roles.map((role, index) => (
                  <motion.div
                    key={role.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className={`relative cursor-pointer transition-all duration-200 ${formData.volunteer === role.id ? "transform scale-105" : "hover:scale-102"
                      }`}
                    onClick={() => handleInputChange("volunteer", role.id)}
                  >
                    <input
                      type="radio"
                      id={role.id}
                      name="role"
                      value={role.id}
                      checked={formData.volunteer === role.id}
                      onChange={() => handleInputChange("volunteer", role.id)}
                      className="sr-only"
                    />
                    <div
                      className={`p-4 border-2 rounded-lg transition-colors ${formData.volunteer === role.id
                        ? "border-orange-500 bg-orange-50/10"
                        : "border-gray-200/10 hover:border-gray-300"
                        }`}
                    >
                      <h3 className="font-semibold text-gray-100 mb-1">{role.label}</h3>
                      <p className="text-sm text-gray-100/70">{role.description}</p>
                      {formData.volunteer === role.id && (
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute top-2 right-2">
                          <CheckCircle className="w-5 h-5 text-orange-500" />
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              {errors.volunteer && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center mt-2 text-red-600 text-sm"
                >
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.volunteer}
                </motion.div>
              )}
            </motion.div>

            {/* Submit Button */}
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.8 }}>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-orange-600 hover:bg-orange-700 hover:shadow-lg transform hover:scale-105"
                  }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Send className="w-5 h-5 mr-2" />
                    Submit Application
                  </div>
                )}
              </button>
            </motion.div>
          </form>
        </motion.div>
      </div>
    )
  }

  const step1 = () => {
    return (
      <div>
        <div className="bg-white/10 rounded-lg p-6 mb-8 mt-4">
          <h3 className="text-lg font-semibold text-orange-600 mb-4">What's Next?</h3>
          <div className="space-y-3 text-left">
            <div className="flex items-center gap-3 text-gray-300">
              <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
              <span>Keep an eye on your mailbox.</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300">
              <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
              <span>You'll receive a confirmation email within 24 hours</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300">
              <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
              <span>If you'd like to support with your service, tap the button below to volunteer.</span>
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="cursor-pointer">
            <div onClick={() => setShowVolunteer(true)} className="bg-gradient-to-r from-orange-500 to-orange-500 text-white px-6 py-2 text-lg font-semibold mb-4 rounded-lg">
              VOLUNTEER
            </div>
          </div>
          <p className="text-gray-400">
            The Healing Party - TSO Ministries
            <br />
            <span className="text-orange-700 font-semibold">August 28, 2026 • 02:00 PM WAT</span>
          </p>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
    >
      <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-red-500/10 h-full flex flex-col justify-center items-center">
        <h2 className="text-3xl font-bold mb-0">{showVolunteer ? "Volunteer for The Healing Party" : "Registration successful!"}</h2>

        <motion.div
          initial={{ y: 20, opacity: 0, scale: 0, }}
          animate={{ y: 0, opacity: 1, scale: 1, }}
          transition={{ delay: 0 }}
          className='mb-4'
        >
          <motion.div
            initial={{ y: 20, opacity: 1, scale: 1, height: 150, width: 150 }}
            animate={{ y: 0, opacity: 0, scale: 0, height: 0, width: 0 }}
            transition={{ delay: 1.5 }}
            className=' bg-white/10 rounded-full flex justify-center items-center'>
            <Check className='w-[100px] h-[100px] text-orange-600' />
          </motion.div>
        </motion.div>

        {showVolunteer ?
          renderVolunteer()
          :
          step1()}

      </form>
    </motion.div>
  )


}
